import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoginState } from '../../client-redux/login/reducer';
import { AppState } from '../../client-redux/reducers';
import { Group, Button, Grid, GridCol, Table, Container, Title } from '@mantine/core';
import {
  equipmentRemoteOperation,
  fetchEquipmentStatus,
  IEquipmentStatusResponse,
  IRemoteOperation
} from '../../../libraries/equipment';
import { formatTransactionType } from '../../utils/script';
import { Language } from '../../languages/languageHandler';
import ModalComponent from '../modal';
import CustomModal from '../custom-modal';
import { formatDateTimeLabel } from '../../utils/date';
import DisplayField from '../display-field';
import './styles.scss';

interface ShowEquipmentProps {
  eid: number;
  openDetails: boolean;
  hideBtn: () => void;
  lang: Language;
}

export enum OPERATION {
  RESTART = 'RESTART',
  SHUTDOWN = 'SHUTDOWN',
  UNLOCK_ESCROW = 'UNLOCK_ESCROW',
  OUT_OF_SERVICE = 'OUT_OF_SERVICE',
  IN_SERVICE = 'IN_SERVICE',
  OPEN_ESCROW = 'OPEN_ESCROW',
  CLOSE_ESCROW = 'CLOSE_ESCROW',
  VPN_CONNECT = 'VPN_CONNECT',
  VPN_DISCONNECT = 'VPN_DISCONNECT'
}

const ShowEquipment = ({ eid, openDetails, hideBtn, lang }: ShowEquipmentProps) => {
  const login = useSelector<AppState, LoginState>(state => state.login);

  const [message, setMessage] = useState<IEquipmentStatusResponse>();
  const [selectedEid] = useState<number>(eid);
  const controller = new AbortController();

  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (selectedEid) {
      fetchEquipmentStatus(selectedEid).then(response => {
        setMessage(response);
      });
    }
  }, [selectedEid, eid]);

  useEffect(() => {
    if (showSuccessDialog) {
      const timeout = setTimeout(() => {
        setShowSuccessDialog(false);
        setSuccessMessage('');
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [showSuccessDialog]);

  useEffect(() => {
    if (showErrorDialog) {
      const timeout = setTimeout(() => {
        setShowErrorDialog(false);
        setErrorMessage('');
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [showErrorDialog]);

  const handleRemoteOperation = async (eid: number, operation: OPERATION) => {
    const payload: IRemoteOperation = {
      eid,
      operation
    };

    try {
      const response = await equipmentRemoteOperation(controller, payload);

      if (response.statusOper.code !== 0) {
        throw new Error(response.statusOper.message || 'Unknown error occurred.');
      }
      setSuccessMessage(lang.DEPOSIT_EQUIPMENTS_TRANSACTIONS_ALERT_COMMAND || 'Operation completed successfully.');
      setShowSuccessDialog(true);
    } catch (error: any) {
      setErrorMessage(
        `${lang.DEPOSIT_EQUIPMENTS_TRANSACTIONS_ALERT_COMMAND}: ${error.code} - ${error.message}` ||
          'Error occurred while performing operation.'
      );
      setShowErrorDialog(true);
    }
  };

  const getEquipmentStatus = (status?: number) => {
    if (status === undefined) return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;
    switch (status) {
      case null:
        return lang.EQUIPMENT_NO_STATUS;
      case 0:
        return lang.EQUIPMENT_STATUS_LABEL_NOT_DEFINED;
      case 1:
        return lang.EQUIPMENT_STATUS_LABEL_DEFINED;
      case 2:
        return lang.EQUIPMENT_STATUS_LABEL_INICIALIZED;
      case 3:
        return lang.EQUIPMENT_STATUS_LABEL_ONLINE;
      case 4:
        return lang.EQUIPMENT_STATUS_LABEL_OFFLINE;
      default:
        return `UNKNOWN_STATE_${status}`;
    }
  };

  const getStatusLevelClass = (level: string | undefined) => {
    switch (level) {
      case 'OK':
        return 'status-ok';
      case 'WARNING':
        return 'status-warning';
      case 'ERROR':
        return 'status-error';
      default:
        return 'status-unknown';
    }
  };

  return (
    <>
      <ModalComponent
        show={openDetails}
        handleClick={hideBtn}
        title={`EID: ${selectedEid}`}
        classname="equipment-details"
        content={
          <Container classNames={{ root: 'show-equipment-details' }}>
            <Group>
              <Grid>
                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title="Status"
                    content={
                      lang && (
                        <Title className={message?.status?.status === 4 ? 'offlineStatus' : 'onlineStatus'}>
                          {message?.status ? getEquipmentStatus(message?.status.status) : `${lang.EQUIPMENT_NO_STATUS}`}
                        </Title>
                      )
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_STATE}
                    content={
                      <Title className={message?.status?.inService === false ? 'notInService' : 'inService'}>
                        {message?.status
                          ? message?.status?.inService
                            ? `${lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}`
                            : `${lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE}`
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </Title>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_COM_DT}
                    content={
                      <Title>
                        {message?.status?.lastComDt
                          ? formatDateTimeLabel(message?.status.lastComDt)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </Title>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_SOFTWARE_VERSION}
                    content={<Title>{message?.status?.softwareVersion || 'N/A'}</Title>}
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_OPER}
                    content={
                      <Title>
                        {message?.status?.lastOperation
                          ? formatTransactionType(lang, message?.status.lastOperation)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </Title>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.EQUIPMENT_LAST_OPER_DT}
                    content={
                      <Title>
                        {message?.status?.lastOperationDt
                          ? formatDateTimeLabel(message?.status.lastOperationDt)
                          : `${lang.EQUIPMENT_NO_INFORMATION}`}
                      </Title>
                    }
                  />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField title={lang.GLOBAL_IP} content={<Title>N/A</Title>} />
                </GridCol>

                <GridCol span={{ base: 12, xs: 12, sm: 6, md: 4 }}>
                  <DisplayField
                    title={lang.GLOBAL_IP_LOCAL}
                    content={<Title>{message?.status?.lastLocalIp || 'N/A'}</Title>}
                  />
                </GridCol>
              </Grid>

              {message?.status?.statusList && (
                <Group classNames={{ root: 'equipment-status-table' }}>
                  <Table.ScrollContainer minWidth={500}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Td></Table.Td>
                          <Table.Td></Table.Td>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {message.status.statusList.map((status, index) => (
                          <Table.Tr key={index}>
                            <Table.Th>{lang[status?.idLangKey]}</Table.Th>
                            <Table.Td className={getStatusLevelClass(status?.level)}>
                              {lang[status?.statusLangKey]}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Group>
              )}

              {(login.permissions.hasNonFinancialRemoteOperations() ||
                login.permissions.hasFinancialRemoteOperations()) && (
                <Group className="equip-details-buttons">
                  {login.permissions.hasNonFinancialRemoteOperations() && (
                    <>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.RESTART)}>
                        {lang.EQUIPMENT_STATE_BUTTON_RESTART}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.SHUTDOWN)}>
                        {lang.EQUIPMENT_STATE_BUTTON_SHUTDOWN}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.OUT_OF_SERVICE)}>
                        {lang.EQUIPMENT_STATE_BUTTON_OUT_OF_SERVICE}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.IN_SERVICE)}>
                        {lang.EQUIPMENT_STATE_BUTTON_IN_SERVICE}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.VPN_CONNECT)}>
                        VPN Connect
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.VPN_DISCONNECT)}>
                        VPN Disconnect
                      </Button>
                    </>
                  )}
                  {login.permissions.hasFinancialRemoteOperations() && (
                    <>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.OPEN_ESCROW)}>
                        {lang.EQUIPMENT_STATE_BUTTON_OPEN_ESCROW}
                      </Button>
                      <Button radius="md" onClick={() => handleRemoteOperation(selectedEid, OPERATION.CLOSE_ESCROW)}>
                        {lang.EQUIPMENT_STATE_BUTTON_CLOSE_ESCROW}
                      </Button>
                    </>
                  )}
                </Group>
              )}
            </Group>
          </Container>
        }
      />

      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={successMessage}
        backgroundColor="#3BDF70"
        textColor="black"
      />

      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </>
  );
};

export default ShowEquipment;
